import DOMPurify from "dompurify";

import type {PublicStatusPage} from "@/pages/statuspages/consumption/types.ts";
import type {SharedStatusPageFormData} from "@/pages/statuspages/shared/StatusPage.types.ts";

// Fields of the consumption page that can be patched live from the edit form.
export type StatusPagePreviewOverrides = Partial<PublicStatusPage>;

// postMessage contract between the edit form (parent) and the embedded consumption iframe.
export const LIVE_PREVIEW_READY = "uptime:status-page-live-preview-ready" as const;
export const LIVE_PREVIEW_OVERRIDE = "uptime:status-page-live-preview-override" as const;

export interface LivePreviewReadyMessage {
  type: typeof LIVE_PREVIEW_READY;
}

export interface LivePreviewOverrideMessage {
  type: typeof LIVE_PREVIEW_OVERRIDE;
  overrides: StatusPagePreviewOverrides;
}

export type LivePreviewMessage = LivePreviewReadyMessage | LivePreviewOverrideMessage;

export const isLivePreviewMessage = (data: unknown): data is LivePreviewMessage => {
  if (typeof data !== "object" || data === null) return false;
  const type = (data as {type?: unknown}).type;
  if (type === LIVE_PREVIEW_READY) return true;
  if (type === LIVE_PREVIEW_OVERRIDE) {
    const overrides = (data as {overrides?: unknown}).overrides;
    return typeof overrides === "object" && overrides !== null && !Array.isArray(overrides);
  }
  return false;
};

export const applyPreviewOverrides = (
  statusPage: PublicStatusPage,
  overrides: StatusPagePreviewOverrides | null
): PublicStatusPage => {
  if (!overrides) return statusPage;
  return {...statusPage, ...overrides};
};

// Maps a logo/favicon form value to its preview *_url: a string is a persisted URL, null is an
// explicit removal (both previewable), while a FileList (unsaved upload) or undefined is skipped.
const toPreviewUrl = (value: unknown): string | null | undefined => {
  if (typeof value === "string") return value;
  if (value === null) return null;
  return undefined;
};

// Unsaved custom code is injected as raw markup into the same-origin preview iframe. Saved values
// are sanitized server-side, but live-preview overrides bypass that, so sanitize here before
// sending. HTML goes through DOMPurify. CSS is injected as raw text inside a scoped <style> element
// via dangerouslySetInnerHTML; a raw-text element only ends on `</style` when the name is followed
// by whitespace, `/`, or `>` (matched here by `\b`). Insert a backslash to break that `</` so the
// tag can never close and break out into HTML/JS, while leaving legitimate CSS untouched.
const sanitizeCustomCss = (css: string): string => css.replace(/<\/style\b/gi, "<\\/style");
const sanitizeCustomHtml = (html: string): string => DOMPurify.sanitize(html);

/**
 * Maps watched edit-form values onto the subset of `PublicStatusPage` fields that the
 * consumption page renders. Only fields with a clean form -> preview mapping are included;
 * `description` is intentionally omitted because the preview renders server-side markdown
 * (`description_html`) which cannot be derived on the client.
 */
export const buildPreviewOverrides = (values: Partial<SharedStatusPageFormData>): StatusPagePreviewOverrides => {
  const overrides: StatusPagePreviewOverrides = {};

  if (values.name !== undefined) overrides.name = values.name;
  if (values.timezone !== undefined) {
    overrides.timezone = values.timezone;
    // Consumption components format dates with the server-derived `used_timezone`, so mirror the
    // edited timezone onto it. An empty value means "use the account/server default", which cannot
    // be derived client-side, so `used_timezone` is left as the saved value in that case.
    if (values.timezone) overrides.used_timezone = values.timezone;
  }
  if (values.time_format !== undefined) overrides.time_format = values.time_format as PublicStatusPage["time_format"];
  if (values.company_website_url !== undefined) overrides.company_website_url = values.company_website_url;
  if (values.theme !== undefined) overrides.theme = values.theme as PublicStatusPage["theme"];
  if (values.layout_preset !== undefined) overrides.layout_preset = values.layout_preset;
  if (values.uptime_calculation_type !== undefined) {
    overrides.uptime_calculation_type = values.uptime_calculation_type as PublicStatusPage["uptime_calculation_type"];
  }

  if (values.custom_header_bg_color_hex !== undefined) {
    overrides.custom_header_bg_color_hex = values.custom_header_bg_color_hex;
  }
  if (values.custom_header_text_color_hex !== undefined) {
    overrides.custom_header_text_color_hex = values.custom_header_text_color_hex;
  }

  // Inspire custom code. The preview renders these as raw markup, mirroring the consumption page,
  // but unlike saved values these overrides are not sanitized server-side, so sanitize them here.
  if (values.custom_css_inspire !== undefined) {
    overrides.custom_css_inspire = sanitizeCustomCss(values.custom_css_inspire);
  }
  if (values.custom_header_html_inspire !== undefined) {
    overrides.custom_header_html_inspire = sanitizeCustomHtml(values.custom_header_html_inspire);
  }
  if (values.custom_footer_html_inspire !== undefined) {
    overrides.custom_footer_html_inspire = sanitizeCustomHtml(values.custom_footer_html_inspire);
  }
  // Legacy custom code: the same trio for LEGACY-themed pages, which the consumption page renders
  // through the Inspire layout with its own field set.
  if (values.custom_css !== undefined) overrides.custom_css = sanitizeCustomCss(values.custom_css);
  if (values.custom_header_html !== undefined) {
    overrides.custom_header_html = sanitizeCustomHtml(values.custom_header_html);
  }
  if (values.custom_footer_html !== undefined) {
    overrides.custom_footer_html = sanitizeCustomHtml(values.custom_footer_html);
  }

  // logo/favicon form fields are a FileList during an unsaved upload (not previewable). A string is
  // a persisted URL and null is an explicit removal — both should be reflected in the live preview.
  const logoUrl = toPreviewUrl(values.logo);
  if (logoUrl !== undefined) overrides.logo_url = logoUrl;
  const faviconUrl = toPreviewUrl(values.favicon);
  if (faviconUrl !== undefined) overrides.favicon_url = faviconUrl;

  if (values.default_status_date_range !== undefined) {
    overrides.default_status_date_range = values.default_status_date_range;
  }
  if (values.default_history_date_range !== undefined) {
    overrides.default_history_date_range = values.default_history_date_range;
  }

  const booleanFields: (keyof PublicStatusPage & keyof SharedStatusPageFormData)[] = [
    "show_status_tab",
    "show_history_tab",
    "show_active_incidents",
    "show_component_response_time",
    "show_summary_metrics",
    "show_past_incidents",
    "show_component_history",
    "show_history_snake",
    "hide_empty_tabs_status",
    "hide_empty_tabs_history",
    "allow_drill_down",
    "allow_pdf_report",
  ];

  for (const field of booleanFields) {
    const value = values[field];
    if (typeof value === "boolean") {
      (overrides as Record<string, boolean>)[field] = value;
    }
  }

  return overrides;
};
